import { supabase } from "./supabase";
import type { AllowedUser, VmStatusResponse } from "./types";

const BASE = "/api";

async function authedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  const headers = new Headers(init.headers);
  if (token) headers.set("Authorization", `Bearer ${token}`);
  if (init.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  return fetch(`${BASE}${path}`, { ...init, headers });
}

async function publicFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  return fetch(`${BASE}${path}`, { ...init, headers });
}

async function readJson<T>(res: Response): Promise<T> {
  const contentType = res.headers.get("content-type") ?? "";
  const isJson = contentType.includes("application/json");
  const text = await res.text();
  let body: unknown = null;
  if (isJson && text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = null;
    }
  }
  if (!res.ok) {
    const jsonError =
      body && typeof body === "object" && "error" in body && typeof (body as { error: unknown }).error === "string"
        ? (body as { error: string }).error
        : null;
    const message = jsonError ?? `HTTP ${res.status} — response was not JSON (got ${contentType || "unknown content-type"})`;
    const err = new Error(message) as Error & { status: number };
    err.status = res.status;
    throw err;
  }
  return body as T;
}

export const api = {
  status: () => authedFetch("/status").then(readJson<VmStatusResponse>),
  start: () => authedFetch("/start", { method: "POST" }).then(readJson<{ ok: true }>),
  stop: () => authedFetch("/stop", { method: "POST" }).then(readJson<{ ok: true }>),
  suspend: () => authedFetch("/suspend", { method: "POST" }).then(readJson<{ ok: true }>),
  resume: () => authedFetch("/resume", { method: "POST" }).then(readJson<{ ok: true }>),
  bootstrapStatus: () =>
    publicFetch("/bootstrap-status").then(readJson<{ initialized: boolean }>),
  listUsers: () => authedFetch("/users").then(readJson<{ users: AllowedUser[] }>),
  addUser: (email: string, opts?: { bootstrap?: boolean }) => {
    const fetcher = opts?.bootstrap ? publicFetch : authedFetch;
    return fetcher("/users", {
      method: "POST",
      body: JSON.stringify({ email }),
    }).then(readJson<{ user: AllowedUser }>);
  },
  removeUser: (email: string) =>
    authedFetch(`/users?email=${encodeURIComponent(email)}`, {
      method: "DELETE",
    }).then(readJson<{ ok: true }>),
};
